import { useFitnessStore } from '../stores/fitnessStore';
import type { DisciplineAttachment } from '../types';

/**
 * Discipline XP attribution (DS2, spec §5) — "one earning event, many
 * attributions, NO new mint."
 *
 * An action (habit / routine / program / task) earns its normal domain XP once.
 * When it's attached to the Fitness discipline, the *same* XP is mirrored into
 * the separate Fitness parameter ledger (`fitness_parameters`, which is NOT part
 * of the user/domain total in `useUserStats`) — so the user/domain total is
 * never inflated. Major performance parameters get the full XP (they drive the
 * discipline's level); minor muscle groups get a light fraction (coverage for
 * the body-map, without dominating the level).
 */

/** Minor body parts get this fraction of the action's XP (coverage, light weight). */
export const MINOR_BODY_PART_WEIGHT = 0.25;

export interface AttributionEntry {
  parameterName: string;
  xp: number;
}

/**
 * Plan how an action's earned XP attributes to its Fitness parameters. Pure —
 * the level math lives in `fitnessStore.addXP`; this just decides what gets how
 * much. Only Fitness is parameter-backed today; other disciplines short-circuit.
 */
export function planDisciplineAttribution(
  attachment: DisciplineAttachment | undefined,
  xpAmount: number
): AttributionEntry[] {
  if (!attachment || attachment.disciplineId !== 'fitness') return [];
  if (!Number.isFinite(xpAmount) || xpAmount <= 0) return [];

  const entries: AttributionEntry[] = [];
  const major = Math.round(xpAmount);
  for (const name of attachment.majorParameters ?? []) {
    if (name) entries.push({ parameterName: name, xp: major });
  }

  const minor = Math.round(xpAmount * MINOR_BODY_PART_WEIGHT);
  if (minor > 0) {
    for (const name of attachment.minorBodyParts ?? []) {
      if (name) entries.push({ parameterName: name, xp: minor });
    }
  }
  return entries;
}

/**
 * Apply the planned attribution to the Fitness ledger (fire-and-forget). Ensures
 * the parameter set is loaded first (a completion can fire before the Fitness
 * surface was ever opened), then applies sequentially to avoid reload thrash on
 * the shared set. Safe no-op when there's nothing to attribute.
 */
export async function attributeDisciplineXP(
  attachment: DisciplineAttachment | undefined,
  xpAmount: number,
  source?: string
): Promise<void> {
  const plan = planDisciplineAttribution(attachment, xpAmount);
  if (plan.length === 0) return;

  const store = useFitnessStore.getState();
  if (store.parameters.length === 0) {
    if (!store.isInitialized) await store.initializeParameters();
    await store.loadParameters();
  }

  const { addXP } = useFitnessStore.getState();
  for (const entry of plan) {
    try {
      await addXP(entry.parameterName, entry.xp, source);
    } catch (e) {
      console.error('Discipline XP attribution failed:', e);
    }
  }
}
